import {
  dynamicTime,
  shortTimestamp,
  dateFormat,
  getCurrentLocale,
} from 'shared/helpers/timeHelper';
import { formatDistanceToNow } from 'date-fns';

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const MONTH = 30 * DAY;
const YEAR = 365 * DAY;

// THIRTY_DAYS is a distinct role from MONTH above — don't DRY-merge them.
const THIRTY_DAYS_IN_MILLI_SECONDS = 30 * 24 * 60 * 60 * 1000;

// [size, suffix, promote-at]: promote to the next unit only past the boundary (59¾m→"1h", not "60m").
const SLA_UNITS = [
  [MINUTE, 'm', 60],
  [HOUR, 'h', 24],
  [DAY, 'd', 30],
  [MONTH, 'mo', 12],
  [YEAR, 'y', Infinity],
];

export const SLA_WARNING_WINDOW_MS = 30 * 60 * 1000;

const shortDuration = epochSeconds =>
  shortTimestamp(dynamicTime(epochSeconds)).trim();

export const conversationCardDuration = (
  createdAtTimestamp,
  lastActivityTimestamp
) => {
  if (!createdAtTimestamp) {
    return lastActivityTimestamp ? shortDuration(lastActivityTimestamp) : '';
  }
  const createdAt = shortDuration(createdAtTimestamp);
  if (!lastActivityTimestamp) return createdAt;
  return `${createdAt} • ${shortDuration(lastActivityTimestamp)}`;
};

export const conversationTimestampTooltip = (
  { createdAt, lastActivityAt },
  t,
  i18n
) => {
  const locale = i18n ? getCurrentLocale(i18n) : undefined;
  const line = (epoch, recentKey, oldKey) => {
    if (!epoch) return '';
    const isOld = Date.now() - epoch * 1000 > THIRTY_DAYS_IN_MILLI_SECONDS;
    return isOld
      ? `${t(oldKey)} ${dateFormat(epoch)}`
      : `${t(recentKey)} ${formatDistanceToNow(epoch * 1000, {
          addSuffix: false,
          locale,
        })}`;
  };
  return [
    line(
      createdAt,
      'CHAT_LIST.CHAT_TIME_STAMP.CREATED.LATEST',
      'CHAT_LIST.CHAT_TIME_STAMP.CREATED.OLDEST'
    ),
    line(
      lastActivityAt,
      'CHAT_LIST.CHAT_TIME_STAMP.LAST_ACTIVITY.ACTIVE',
      'CHAT_LIST.CHAT_TIME_STAMP.LAST_ACTIVITY.NOT_ACTIVE'
    ),
  ]
    .filter(Boolean)
    .join('\n');
};

export const hasSlaData = sla =>
  Boolean(
    sla &&
      (sla.first_response_time || sla.next_response_time || sla.time_to_resolve)
  );

export const compactSlaTime = timestamp => {
  if (!timestamp) return null;
  const deltaMs = Math.abs(Date.now() - timestamp * 1000);
  if (deltaMs < MINUTE) return 'now';
  const [unit, suffix] = SLA_UNITS.find(
    ([size, , promoteAt]) => Math.round(deltaMs / size) < promoteAt
  );
  return `${Math.round(deltaMs / unit)}${suffix}`;
};
